import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job, { Collaborator } from 'jobs/Job'
import JobStore from 'jobs/JobStore'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Id } from 'types/definitions'
import { required, validationContext } from 'validx'

export default class RemoveClientsAsNonAdminViewModel extends ModalViewModel {
  /**
   * Search text.
   */
  @observable searchText = ''

  /**
   * Collaborator to remove.
   */
  @observable collaborator?: Collaborator = undefined

  /**
   * Disables submit button.
   */
  @observable disabled = true

  /**
   * Validation context for View Model.
   */
  validation = validationContext(this, {
    collaborator: [required('Collaborator is required.')],
  })

  constructor(
    private job: Job,
    private jobStore: JobStore,
    private newAlert: AlertFactory,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Supporting Clients on the Job.
   */
  @computed
  get supportingClients() {
    return this.job.supportingClients
  }

  /**
   * Filtered collaborators.
   */
  @computed
  get filteredCollaborators() {
    const lowerSearchText = this.searchText.toLowerCase()
    return this.supportingClients.filter(
      (supportingCollaborator: Collaborator) => {
        const shouldBeRemoved = this.job.collaborators.some(
          (c) =>
            c.member === supportingCollaborator.member &&
            c.role === 'SUPPORTING',
        )

        return (
          shouldBeRemoved &&
          supportingCollaborator.member.displayName
            .toLowerCase()
            .includes(lowerSearchText)
        )
      },
    )
  }

  /**
   * Sets the search text.
   */
  @action.bound
  setSearchText(newText: string) {
    this.searchText = newText
  }

  /**
   * Sets a collaborator to remove.
   */
  @action.bound
  setCollaborator(collaborator?: Collaborator) {
    this.collaborator = collaborator
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.collaborator = undefined
    this.disabled = true

    this.removeClient.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Removes a client from the job.
   */
  removeClient = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    const collaborator = this.job.collaborators.find(
      (c) => c.member === this.collaborator?.member,
    )

    if (collaborator) {
      const alert = this.newAlert()
        .withState(AlertState.Pending)
        .saying(
          'Removing Client',
          `Removing ${collaborator.member.displayName} from ${this.job.name}...`,
        )
        .show()

      await this.jobStore.removeClientAsNonAdmin(
        Id.assert(this.job.id),
        Id.assert(collaborator.member.user.publicId),
      )

      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${collaborator.member.displayName} was removed from ${this.job.name}.`,
        )
        .autoDismiss()
    }

    this.close()
  })

  /**
   * Shows RemoveClientsAsNonAdminDialog.
   */
  show() {
    if (this.showing) {
      return
    }

    super.show()
  }
}
